// @ts-nocheck
import {
  Settings,
  ChevronUp,
  ChevronDown,
  Brain,
  ChevronRight,
} from "lucide-react";
import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { createPortal } from "react-dom";

import {
  getCategoryLabel,
  KPI_CATEGORIES,
} from "../../constants/kpiCategories";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Dialog, DialogContent } from "../../components/ui/dialog";
import {
  useHawkeyeSorting,
  HawkeyeSortField,
} from "../../hooks/hawkeye/useHawkeyeSorting";
import { useIsMobile } from "../../hooks/use-mobile";
import type {
  HawkeyeChartMetricsProps,
  HawkeyeMetricsAccountsInput,
} from "../../hooks/useHawkeyeMetrics";
import { cn } from "../../lib/utils";
import { useScrollChainRef } from "../../../Hooks/useScrollChain";

import { BubbleCell } from "./BubbleCell";
import KpiCompareSelectionStrip from "./KpiCompareSelectionStrip";
import KpiCompareMissingDataModal, {
  getSelectedAccountsWithoutKpiData,
} from "./KpiCompareMissingDataModal";
import SpiderCompareModalContent from "./SpiderCompareModalContent";
import useXFactorKpiFullBriefing from "../../../Hooks/useXFactorKpiFullBriefing";
import KpiCategoryDetailModal from "../../../Pages/TempViewsRendering/components/KpiCategoryDetailModal";

type HawkeyeDataSource = "hawkeye" | "digitalocean";

interface HawkeyeMetricsBubbleChartProps {
  selectedKPIs?: string[];
  measurementDate?: string;
  metricsAccountsInput?: HawkeyeMetricsAccountsInput;
  hawkeyeMetrics: HawkeyeChartMetricsProps;
  highlightedAccountIds?: string[];
  onHighlightedAccountIdsChange?: (accountIds: string[]) => void;
  onSaveAsWatchlist?: (accountIds: string[]) => void;
  selectionPanelRef?: React.RefObject<HTMLElement | null>;
  /** When false, only show accounts with no KPI data. */
  showUnavailableKpiData?: boolean;
  /**
   * Progressive row/dot reveal. Keep true for the first Always On page;
   * later pages should show rows immediately.
   */
  enableRowReveal?: boolean;
  /** Fired once when progressive reveal finishes (or is skipped). */
  onRevealComplete?: () => void;
  /**
   * When set, the selection strip portals here (below pagination in Always On).
   */
  selectionStripHost?: HTMLElement | null;
  dataSource?: HawkeyeDataSource;
  generateDate?: string | null;
  generatingAccountId?: string | null;
  briefedAccountIds?: Set<string>;
  onGenerateAccount?: (accountId: string) => void;
}

interface MetricColumn {
  metric_id: string;
  metric_name: string;
  category_id: string;
}

const toCompactHeaderLabel = (label: string) =>
  String(label || "")
    .replace(/\s+/g, " ")
    .replace(/\s*\/\s*/g, "/")
    .trim();

/** Left border marking the start of each category group (after the first) */
const CATEGORY_BORDER_CLASS = "border-l border-l-slate-300";

const areAccountSelectionsEqual = (left: string[], right: string[]) =>
  left.length === right.length && left.every((accountId, index) => accountId === right[index]);

export const HawkeyeMetricsBubbleChart: React.FC<
  HawkeyeMetricsBubbleChartProps
> = ({
  selectedKPIs,
  measurementDate,
  metricsAccountsInput,
  hawkeyeMetrics,
  highlightedAccountIds = [],
  onHighlightedAccountIdsChange,
  onSaveAsWatchlist,
  selectionPanelRef,
  showUnavailableKpiData = true,
  enableRowReveal = true,
  onRevealComplete,
  selectionStripHost = null,
  dataSource = "hawkeye",
  generateDate = null,
  generatingAccountId = null,
  briefedAccountIds,
  onGenerateAccount,
}) => {
  const isDigitalocean = dataSource === "digitalocean";
  const MIN_COMPARE_ACCOUNTS = 2;
  const COMPARE_COLORS = ["#7c3aed", "#2563eb", "#0d9488", "#d97706"];
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const setScrollAreaRef = useScrollChainRef(scrollAreaRef);
  const isMobile = useIsMobile();
  const [nativeDetail, setNativeDetail] = useState<{
    accountId: string;
    categoryId: string;
  } | null>(null);

  // Progressive reveal state
  const [visibleRowCount, setVisibleRowCount] = useState(0);
  const [showBuildingModal, setShowBuildingModal] = useState(false);
  const [modalProgress, setModalProgress] = useState(0);
  const prevLoadingRef = useRef(false);
  const revealTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const graceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevMeasurementDateRef = useRef(measurementDate);
  const hasShownInitialModal = useRef(false);
  const revealCompleteNotifiedRef = useRef(false);
  const onRevealCompleteRef = useRef(onRevealComplete);
  onRevealCompleteRef.current = onRevealComplete;

  const notifyRevealComplete = () => {
    if (revealCompleteNotifiedRef.current) return;
    revealCompleteNotifiedRef.current = true;
    onRevealCompleteRef.current?.();
  };

  // Expand/collapse synopsis state
  const [expandedAccounts, setExpandedAccounts] = useState<Set<string>>(
    new Set(),
  );
  const [allExpanded, setAllExpanded] = useState(false);
  const [selectedCompareAccountIds, setSelectedCompareAccountIds] = useState<
    string[]
  >([]);
  const [isCompareModalOpen, setIsCompareModalOpen] = useState(false);
  const [isMissingKpiDataModalOpen, setIsMissingKpiDataModalOpen] =
    useState(false);
  const [compareSelectionMessage, setCompareSelectionMessage] = useState<
    string | null
  >(null);

  useEffect(() => {
    setSelectedCompareAccountIds(prevIds =>
      areAccountSelectionsEqual(prevIds, highlightedAccountIds)
        ? prevIds
        : highlightedAccountIds
    );
  }, [highlightedAccountIds]);

  useEffect(() => {
    if (selectedCompareAccountIds.length === 0) {
      setCompareSelectionMessage(null);
      return;
    }
    if (selectedCompareAccountIds.length < MIN_COMPARE_ACCOUNTS) {
      setCompareSelectionMessage("Select at least 2 accounts to compare.");
      return;
    }
    setCompareSelectionMessage(null);
  }, [selectedCompareAccountIds.length]);

  // Listen for configure metrics event from header
  useEffect(() => {
    const handleConfigureMetrics = () => {
      setIsConfigModalOpen(true);
    };
    window.addEventListener("hawkeyeConfigureMetrics", handleConfigureMetrics);
    return () =>
      window.removeEventListener(
        "hawkeyeConfigureMetrics",
        handleConfigureMetrics,
      );
  }, []);

  const preferences = null;
  const updatePreferences = () => Promise.resolve();

  const {
    matrixData,
    configuration,
    loading,
    error,
    updateConfiguration,
    refetch,
    reloadConfiguration,
  } = hawkeyeMetrics;

  const { sortedData, sortConfig, handleSort } = useHawkeyeSorting(
    matrixData,
    highlightedAccountIds,
    showUnavailableKpiData
  );

  const nativeAccountIndex = nativeDetail
    ? sortedData.findIndex(
        a => String(a.account_id) === String(nativeDetail.accountId)
      )
    : -1;
  const nativeAccountName =
    nativeAccountIndex >= 0
      ? sortedData[nativeAccountIndex].account_name
      : "";
  const {
    briefing: nativeBriefing,
    loading: nativeBriefingLoading,
    error: nativeBriefingError,
  } = useXFactorKpiFullBriefing(
    isDigitalocean ? nativeDetail?.accountId : null,
    measurementDate || null
  );

  // All metric columns from configuration (unfiltered)
  const allMetricColumns = useMemo<MetricColumn[]>(() => {
    const columns: MetricColumn[] = [];
    const includedCategories = configuration.includedCategories;

    for (const categoryId of includedCategories) {
      const includedMetricIds = configuration.includedMetrics[categoryId] || [];
      const accountWithData = matrixData.find((a) => a.hasData);
      for (const metricId of includedMetricIds) {
        const metricData = accountWithData?.rawMetrics.find(
          (m) => m.metric_id === metricId,
        );
        columns.push({
          metric_id: metricId,
          metric_name: metricData?.metric_name || metricId,
          category_id: categoryId,
        });
      }
    }
    return columns;
  }, [
    configuration.includedCategories,
    configuration.includedMetrics,
    matrixData,
  ]);

  // Default selection: include all configured metrics in category order
  const defaultMetricIds = useMemo(() => {
    const ids: string[] = [];
    for (const categoryId of configuration.includedCategories) {
      const catMetrics = allMetricColumns.filter(
        (m) => m.category_id === categoryId,
      );
      ids.push(...catMetrics.map((m) => m.metric_id));
    }
    return ids;
  }, [configuration.includedCategories, allMetricColumns]);

  // Active selection from preferences or defaults
  const activeMetricIds = useMemo(() => {
    const saved = preferences?.metrics_bubble_selected;
    if (saved && saved.length > 0) {
      // Filter to only still-valid metric IDs
      const allIds = new Set(allMetricColumns.map((m) => m.metric_id));
      const valid = saved.filter((id) => allIds.has(id));
      return valid.length > 0 ? valid : defaultMetricIds;
    }
    return defaultMetricIds;
  }, [
    preferences?.metrics_bubble_selected,
    allMetricColumns,
    defaultMetricIds,
  ]);

  // Filtered metric columns
  const metricColumns = useMemo<MetricColumn[]>(() => {
    const activeSet = new Set(activeMetricIds);
    return allMetricColumns.filter((m) => activeSet.has(m.metric_id));
  }, [allMetricColumns, activeMetricIds]);

  const handleSaveMetricSelection = useCallback(
    async (metricIds: string[] | null) => {
      await updatePreferences({ metrics_bubble_selected: metricIds });
    },
    [updatePreferences],
  );

  // Group metric columns by category for the sub-header
  const categoryGroups = useMemo(() => {
    const groups: {
      category_id: string;
      label: string;
      color: string;
      count: number;
    }[] = [];
    let currentCat = "";
    for (const col of metricColumns) {
      if (col.category_id !== currentCat) {
        currentCat = col.category_id;
        groups.push({
          category_id: col.category_id,
          label: getCategoryLabel(col.category_id),
          color: KPI_CATEGORIES[col.category_id]?.color || "hsl(0 0% 50%)",
          count: 1,
        });
      } else {
        groups[groups.length - 1].count++;
      }
    }
    return groups;
  }, [metricColumns]);

  const categoryBoundaryMetricIds = useMemo(() => {
    const ids = new Set<string>();
    let prevCategory = "";
    let isFirstCategory = true;
    for (const col of metricColumns) {
      if (col.category_id !== prevCategory) {
        if (!isFirstCategory) {
          ids.add(col.metric_id);
        }
        prevCategory = col.category_id;
        isFirstCategory = false;
      }
    }
    return ids;
  }, [metricColumns]);

  const categoryLastMetricIds = useMemo(() => {
    const ids = new Set<string>();
    for (let i = 0; i < metricColumns.length; i++) {
      const col = metricColumns[i];
      const next = metricColumns[i + 1];
      if (!next || next.category_id !== col.category_id) {
        ids.add(col.metric_id);
      }
    }
    return ids;
  }, [metricColumns]);

  const selectedCompareAccounts = useMemo(
    () =>
      sortedData.filter((account) =>
        selectedCompareAccountIds.includes(account.account_id),
      ),
    [sortedData, selectedCompareAccountIds],
  );

  const selectedAccountsWithoutKpiData = useMemo(
    () => getSelectedAccountsWithoutKpiData(selectedCompareAccounts),
    [selectedCompareAccounts]
  );

  const compareCategories = useMemo(
    () => categoryGroups.map((group) => group.category_id),
    [categoryGroups],
  );

  const handleCompareClick = () => {
    if (selectedCompareAccountIds.length < MIN_COMPARE_ACCOUNTS) {
      setCompareSelectionMessage("Select at least 2 accounts to compare.");
      return;
    }
    if (selectedAccountsWithoutKpiData.length > 0) {
      setIsMissingKpiDataModalOpen(true);
      return;
    }
    setIsCompareModalOpen(true);
  };

  const handleRemoveAccountsWithoutDataAndCompare = () => {
    const nextIds = selectedCompareAccounts
      .filter(account => account.hasData !== false)
      .map(account => String(account.account_id));

    setIsMissingKpiDataModalOpen(false);
    setSelectedCompareAccountIds(nextIds);
    onHighlightedAccountIdsChange?.(nextIds);

    if (nextIds.length < MIN_COMPARE_ACCOUNTS) {
      setCompareSelectionMessage(
        `At least ${MIN_COMPARE_ACCOUNTS} accounts with KPIs data are required to compare.`
      );
      return;
    }

    setIsCompareModalOpen(true);
  };

  const toggleCompareSelection = (accountId: string) => {
    setSelectedCompareAccountIds((prev) => {
      let nextIds = prev;
      if (prev.includes(accountId)) {
        if (isCompareModalOpen && prev.length <= MIN_COMPARE_ACCOUNTS) {
          setCompareSelectionMessage(
            "At least 2 accounts are required in comparison."
          );
          return prev;
        }
        nextIds = prev.filter((id) => id !== accountId);
      } else {
        nextIds = [...prev, accountId];
      }

      onHighlightedAccountIdsChange?.(nextIds);
      return nextIds;
    });
  };

  // Track loading transitions (same pattern as Categories view)
  useEffect(() => {
    if (!enableRowReveal || revealCompleteNotifiedRef.current) {
      setShowBuildingModal(false);
      setVisibleRowCount(sortedData.length);
      if (sortedData.length) notifyRevealComplete();
      return;
    }

    const dateChanged = prevMeasurementDateRef.current !== measurementDate;
    prevMeasurementDateRef.current = measurementDate;

    if (graceTimerRef.current) {
      clearTimeout(graceTimerRef.current);
      graceTimerRef.current = null;
    }

    if (loading) {
      const isFirstLoad =
        !hasShownInitialModal.current && sortedData.length === 0;
      const isMeaningfulChange = dateChanged;
      if (isFirstLoad || isMeaningfulChange) {
        graceTimerRef.current = setTimeout(() => {
          setShowBuildingModal(true);
          setVisibleRowCount(0);
          setModalProgress(0);
          hasShownInitialModal.current = true;
          if (revealTimerRef.current) {
            clearInterval(revealTimerRef.current);
            revealTimerRef.current = null;
          }
        }, 150);
      }
    }

    if (!loading && prevLoadingRef.current) {
      if (graceTimerRef.current) {
        clearTimeout(graceTimerRef.current);
        graceTimerRef.current = null;
        setVisibleRowCount(sortedData.length);
      } else if (showBuildingModal) {
        setShowBuildingModal(false);
        setVisibleRowCount(sortedData.length);
      }
    }

    prevLoadingRef.current = loading;
    return () => {
      if (graceTimerRef.current) {
        clearTimeout(graceTimerRef.current);
        graceTimerRef.current = null;
      }
    };
  }, [
    loading,
    measurementDate,
    sortedData.length,
    showBuildingModal,
    enableRowReveal,
  ]);

  // Animate modal progress bar
  useEffect(() => {
    if (!enableRowReveal || !showBuildingModal) return;
    const interval = setInterval(() => {
      setModalProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 1;
      });
    }, 50);
    return () => clearInterval(interval);
  }, [showBuildingModal, enableRowReveal]);

  const step1Progress = Math.min(100, Math.max(0, (modalProgress / 33) * 100));
  const step2Progress = Math.min(
    100,
    Math.max(0, ((modalProgress - 33) / 33) * 100),
  );
  const step3Progress = Math.min(
    100,
    Math.max(0, ((modalProgress - 66) / 34) * 100),
  );

  // Progressive row reveal timer
  useEffect(() => {
    if (!enableRowReveal || revealCompleteNotifiedRef.current) {
      setVisibleRowCount(sortedData.length);
      if (sortedData.length) notifyRevealComplete();
      return;
    }
    if (loading || showBuildingModal) return;
    if (!sortedData.length || visibleRowCount >= sortedData.length) {
      if (sortedData.length && visibleRowCount >= sortedData.length) {
        notifyRevealComplete();
      }
      return;
    }

    revealTimerRef.current = setInterval(() => {
      setVisibleRowCount((prev) => {
        const next = prev + 1;
        if (next >= sortedData.length) {
          if (revealTimerRef.current) clearInterval(revealTimerRef.current);
          revealTimerRef.current = null;
          notifyRevealComplete();
        }
        return next;
      });
    }, 200);

    return () => {
      if (revealTimerRef.current) {
        clearInterval(revealTimerRef.current);
        revealTimerRef.current = null;
      }
    };
  }, [
    loading,
    showBuildingModal,
    sortedData.length,
    visibleRowCount,
    enableRowReveal,
  ]);

  // Accounts | Score | …metrics — desktop: metric cols use minmax(floor, 1fr) so they expand to fill the row; when sum of mins exceeds the viewport, the scroll area scrolls. Account/Score capped so growth goes mostly to metrics.
  const gridTemplateColumns = useMemo(() => {
    if (isMobile) {
      const accountColumn = "minmax(10rem, 1.1fr)";
      const scoreColumn = "minmax(3.75rem, 0.65fr)";
      const metricCols = metricColumns
        .map(() => "minmax(5.5rem, 1fr)")
        .join(" ");
      return `${accountColumn} ${scoreColumn} ${metricCols}`;
    }
    const accountColumn = "minmax(12.5rem, 15rem)";
    const scoreColumn = "minmax(3.75rem, 7.5rem)";
    const metricCols = metricColumns
      .map(() => "minmax(3.75rem, 1fr)")
      .join(" ");
    return `${accountColumn} ${scoreColumn} ${metricCols}`;
  }, [metricColumns, isMobile]);

  const accountStickyLeft = isMobile ? "10rem" : "12.5rem";
  const accountColumnMinWidth = isMobile ? "min-w-[10rem]" : "min-w-[12.5rem]";

  const getSortIcon = (field: HawkeyeSortField) => {
    if (!sortConfig || sortConfig.field !== field) return null;
    return sortConfig.direction === "asc" ? (
      <ChevronUp className="h-4 w-4 inline ml-1" />
    ) : (
      <ChevronDown className="h-4 w-4 inline ml-1" />
    );
  };

  // Expand/collapse helpers
  const expandableAccounts = useMemo(
    () => sortedData.filter((a) => a.hasData),
    [sortedData],
  );

  const toggleAccountExpand = (accountId: string) => {
    setExpandedAccounts((prev) => {
      const next = new Set(prev);
      if (next.has(accountId)) next.delete(accountId);
      else next.add(accountId);
      return next;
    });
  };

  const toggleAllExpanded = () => {
    if (allExpanded) {
      setExpandedAccounts(new Set());
      setAllExpanded(false);
    } else {
      setExpandedAccounts(new Set(expandableAccounts.map((a) => a.account_id)));
      setAllExpanded(true);
    }
  };

  const getMetricValueForAccount = (account: any, metricId: string): number => {
    const metric = account.rawMetrics.find((m) => m.metric_id === metricId);
    return metric?.normalized_value ?? 0;
  };

  const hasMechanicalGridContent = sortedData.some(
    (account) =>
      account.hasData || (account.rawMetrics && account.rawMetrics.length > 0),
  );
  const awaitingDigestWhileMetricsLoaded =
    loading && sortedData.length > 0 && hasMechanicalGridContent;

  const isInitialLoadingState =
    (loading &&
      (sortedData.length === 0 ||
        sortedData.every(
          (account) =>
            !account.hasData &&
            (!account.rawMetrics || account.rawMetrics.length === 0),
        ))) ||
    awaitingDigestWhileMetricsLoaded;

  if (isInitialLoadingState) {
    return (
      <div className="bg-white border-slate-200 rounded-md shadow-sm overflow-hidden p-3">
        <div className="p-0">
          <div className="text-[11px] font-semibold tracking-[0.18em] text-center text-slate-400 py-3 border-b border-border/60">
            Loading...
          </div>
          <div className="space-y-0">
            {Array.from({ length: 6 }).map((_, rowIdx) => (
              <div
                key={`kpi-skeleton-row-${rowIdx}`}
                className="border-b border-border/40 px-4 py-3"
              >
                <div className="grid grid-cols-12 gap-3 items-center">
                  <div className="col-span-2 h-5 rounded bg-slate-200/70 animate-pulse" />
                  {Array.from({ length: 8 }).map((__, cellIdx) => (
                    <div
                      key={`kpi-skeleton-cell-${rowIdx}-${cellIdx}`}
                      className="col-span-1 h-4 rounded-full bg-slate-200/70 animate-pulse"
                    />
                  ))}
                  <div className="col-span-2 h-5 rounded bg-slate-200/70 animate-pulse" />
                </div>
                <div className="mt-3 h-4 rounded bg-slate-200/60 animate-pulse" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="m-6">
        <CardContent className="p-6 text-center">
          <h2 className="text-xl font-semibold mb-2 text-destructive">
            Error Loading Metrics
          </h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={refetch} variant="outline" className="mt-2">
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!sortedData.length) {
    return (
      <Card className="m-6">
        <CardContent className="p-6 text-center">
          <h2 className="text-xl font-semibold mb-2">No Metrics Data</h2>
          <p className="text-muted-foreground">
            No metrics data available for the selected accounts and date range.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      {/* AI Building Modal */}
      <Dialog
        open={
          showBuildingModal && sortedData.length > 0 && !isInitialLoadingState
        }
      >
        <DialogContent
          className="sm:max-w-2xl rounded-2xl border-none bg-gradient-to-br from-purple-50/95 via-white/95 to-violet-50/95 backdrop-blur-sm shadow-[0_0_60px_hsl(270_60%_70%/0.25)] [&>button]:hidden"
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <div className="flex flex-col items-center gap-6 py-6 text-center">
            <div className="relative w-20 h-20 flex items-center justify-center">
              <div
                className="absolute inset-0 w-20 h-20 border-[3px] border-transparent border-t-violet-300 rounded-full animate-spin"
                style={{ animationDuration: "2.5s" }}
              />
              <div
                className="absolute inset-2 w-16 h-16 border-[2.5px] border-transparent border-r-violet-200 rounded-full animate-spin"
                style={{
                  animationDuration: "1.8s",
                  animationDirection: "reverse",
                }}
              />
              <div
                className="absolute inset-4 w-12 h-12 border-2 border-transparent border-b-violet-100 rounded-full animate-spin"
                style={{ animationDuration: "1.2s" }}
              />
              <Brain className="h-8 w-8 text-violet-500 animate-pulse relative z-10" />
            </div>
            <h3 className="text-sm font-semibold tracking-[0.3em] text-violet-600 uppercase">
              Generating Intelligence Briefings From Enterprise KPIs
            </h3>
            <div className="w-full px-4">
              <div className="w-full h-2.5 rounded-full bg-violet-100/60 overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-violet-400 to-violet-500 transition-all duration-100 ease-linear"
                  style={{ width: `${modalProgress}%` }}
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4 w-full px-4">
              {[
                {
                  label: "Gathering and Analyzing Your Enterprise KPIs...",
                  progress: step1Progress,
                },
                {
                  label: "Computing Normalized and Benchmarked Metrics...",
                  progress: step2Progress,
                },
                {
                  label: "Organizing Actionable Insights...",
                  progress: step3Progress,
                },
              ].map((step, i) => (
                <div key={i} className="flex flex-col items-center gap-2">
                  <div className="flex items-center gap-1.5">
                    <div
                      className={`w-2 h-2 rounded-full ${step.progress > 0 ? "bg-amber-400" : "bg-muted-foreground/30"} transition-colors`}
                    />
                    <span className="text-[10px] font-medium tracking-wider text-muted-foreground uppercase leading-tight text-center">
                      {step.label}
                    </span>
                  </div>
                  <div className="w-full h-1.5 rounded-full bg-violet-100/60 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-violet-300 transition-all duration-100 ease-linear"
                      style={{ width: `${step.progress}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Matrix Container */}
      <div className="relative isolate z-0 flex h-full min-h-0 flex-1 flex-col">
      <Card className="flex h-full min-h-0 w-full min-w-0 flex-1 flex-col border-0 bg-transparent shadow-none" style={
                selectedCompareAccountIds.length > 0 && !selectionStripHost
                  ? { paddingBottom: "4.5rem" }
                  : undefined
              }>
        <CardContent className="relative flex h-full min-h-0 flex-1 flex-col overflow-hidden p-4">
          <div className="flex h-full min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden rounded-md border border-border bg-card shadow-sm">
            <div
              ref={setScrollAreaRef}
              className="scrollbar-hover min-h-0 flex-1 overflow-auto overscroll-contain bg-white"
              // style={{ maxHeight: "calc(100vh - 5rem)" }}
            >
              <div className="relative w-full box-border">
                {/* Fixed header block (category + metric labels) while rows scroll */}
                <div className="sticky top-0 z-[70] bg-white shadow-[0_1px_0_0_rgb(226_232_240)]">
                {/* Category Group Sub-Header Row */}
                <div
                  className="grid w-full border-b border-slate-200/90 bg-slate-50 justify-items-stretch"
                  style={{ gridTemplateColumns }}
                >
                  {/* Account spacer — sticky, matches row tint */}
                  <div
                    className={cn(
                      "sticky left-0 z-[60] border-b border-slate-200/90 bg-white",
                      accountColumnMinWidth,
                      isMobile
                        ? "min-h-[1.25rem]"
                        : "min-h-[3.25rem] border-r border-slate-200",
                    )}
                    style={{ position: "sticky", left: 0 }}
                  />
                  {/* Score spacer — sticky after account */}
                  <div
                    className={cn(
                      "sticky z-[62] min-w-0 border-b border-l border-r border-slate-200/90 bg-white",
                      isMobile
                        ? "min-h-[1.25rem] px-1 py-1"
                        : "min-h-[3.25rem] px-1 py-1",
                    )}
                    style={{ position: "sticky", left: accountStickyLeft }}
                  />
                  {/* Category group spans */}
                  {(() => {
                    let colIndex = 3; // 1-based: column 1 is account; column 2 is score; metrics start at 3
                    return categoryGroups.map((group, groupIndex) => {
                      const startCol = colIndex;
                      colIndex += group.count;
                      return (
                        <div
                          key={group.category_id}
                          title={group.label}
                          className={cn(
                            "flex min-w-0 items-center justify-center border-b border-slate-200/90 bg-sky-50 px-2 py-1.5 text-center font-semibold uppercase tracking-[0.04em]",
                            groupIndex > 0 && CATEGORY_BORDER_CLASS,
                            isMobile
                              ? "min-h-[1.25rem] py-1 text-[10px] sm:text-xs break-words"
                              : "min-h-[3.25rem] text-[10px] leading-snug sm:text-[11px] whitespace-normal break-words",
                          )}
                          style={{
                            gridColumn: `${startCol} / span ${group.count}`,
                          }}
                        >
                          <span className="flex h-full w-full items-center justify-center text-center leading-tight">
                            {group.label}
                          </span>
                        </div>
                      );
                    });
                  })()}
                </div>

                {/* Metric Name Header Row — desktop: vertical titles; mobile: wrapped horizontal */}
                <div
                  className="grid w-full border-slate-200/90 bg-white justify-items-stretch"
                  style={{ gridTemplateColumns }}
                >
                  <div
                    className={cn(
                      "sticky left-0 z-[60] max-w-full cursor-pointer border-b border-slate-200/90 bg-white text-slate-800 transition-colors hover:bg-slate-50/80",
                      accountColumnMinWidth,
                      isMobile
                        ? "flex min-h-[3rem] items-center justify-center border-r border-slate-200 px-1 py-2 text-center text-xs font-semibold uppercase tracking-wide"
                        : "relative flex min-h-[11rem] flex-col items-center justify-center border-r border-slate-200 px-1",
                    )}
                    onClick={() => handleSort("account_name")}
                    style={{ position: "sticky", left: 0 }}
                  >
                    <div className="flex min-w-0 max-w-full flex-row items-center justify-center gap-0.5">
                      <span className="whitespace-nowrap text-xs font-semibold sm:text-sm">
                        Account
                      </span>
                      {getSortIcon("account_name")}
                    </div>
                  </div>
                  <div
                    className={cn(
                      "sticky z-[62] min-w-0 max-w-full cursor-pointer border-b border-l border-r border-slate-200/70 bg-white text-slate-800 transition-colors hover:bg-white",
                      isMobile
                        ? "flex min-h-[3rem] items-center justify-center px-2 py-2 text-center text-xs font-semibold"
                        : "relative flex min-h-[11rem] flex-col items-center justify-center px-1",
                    )}
                    onClick={() => handleSort("overallScore")}
                    style={{ position: "sticky", left: accountStickyLeft }}
                  >
                    <div className="flex items-center gap-1">
                      <span className="whitespace-nowrap text-xs font-semibold sm:text-sm">
                        Score
                      </span>
                    </div>
                  </div>
                  {metricColumns.map((col) => {
                    const isCategoryBoundary = categoryBoundaryMetricIds.has(
                      col.metric_id,
                    );
                    const isCategoryEnd = categoryLastMetricIds.has(col.metric_id);
                    return (
                    <div
                      key={col.metric_id}
                      className={cn(
                        "cursor-pointer transition-colors min-w-0 max-w-full border-b bg-white hover:bg-slate-50/50",
                        !isCategoryEnd && "border-r border-slate-200/70",
                        isCategoryBoundary && CATEGORY_BORDER_CLASS,
                        isMobile
                          ? "px-1.5 py-2 text-center"
                          : "relative flex min-h-[11rem] flex-col",
                      )}
                      onClick={() => handleSort(col.metric_id)}
                      title={`${col.metric_name} (${getCategoryLabel(col.category_id)})`}
                    >
                      {isMobile ? (
                        <div className="flex min-w-0 w-full max-w-full flex-col items-center justify-center gap-1">
                          <span className="text-[11px] font-medium text-foreground leading-snug w-full min-w-0 text-center break-words [overflow-wrap:break-word] [word-break:normal]">
                            {col.metric_name}
                          </span>
                          {getSortIcon(col.metric_id)}
                        </div>
                      ) : (
                        <>
                          <div className="absolute left-1/2 top-1.5 z-[5] flex -translate-x-1/2 justify-center">
                            {getSortIcon(col.metric_id)}
                          </div>
                          <div className="flex min-h-0 flex-1 items-center justify-center px-2 py-4">
                            <span
                              className="max-h-[9.5rem] overflow-hidden text-ellipsis text-center text-[11px] font-semibold uppercase leading-snug tracking-[0.02em] text-slate-900 select-none [writing-mode:vertical-rl] [text-orientation:mixed]"
                              title={col.metric_name}
                            >
                              {toCompactHeaderLabel(col.metric_name)}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                    );
                  })}
                </div>
                </div>

                {/* Data Rows */}
                {sortedData.slice(0, visibleRowCount).map((account) => {
                  const hasAssessments =
                    account.hasData &&
                    account.hasDigest &&
                    account.aiCategoryAssessments &&
                    Object.keys(account.aiCategoryAssessments).length > 0;
                  const isExpanded = expandedAccounts.has(account.account_id);
                  return (
                    <React.Fragment key={account.account_id}>
                      <div
                        className={cn(
                          "group grid w-full animate-fade-in justify-items-stretch bg-white transition-colors"
                        )}
                        style={{ gridTemplateColumns }}
                      >
                        {/* Account Name — sticky while scrolling horizontally */}
                        <div
                          className={cn(
                            "sticky left-0 z-[55] flex max-w-full items-center justify-start gap-2.5 border-r border-slate-200 bg-white px-2.5 py-2.5 group-hover:bg-white",
                            accountColumnMinWidth,
                          )}
                          style={{ position: "sticky", left: 0 }}
                        >
                          <label className="relative inline-flex shrink-0 cursor-pointer items-center justify-center">
                            <input
                              type="checkbox"
                              aria-label={`Select ${account.account_name} for comparison`}
                              checked={selectedCompareAccountIds.includes(
                                account.account_id,
                              )}
                              onChange={() =>
                                toggleCompareSelection(account.account_id)
                              }
                              className="peer sr-only"
                            />
                            <span className="h-4 w-4 rounded-[4px] border border-slate-300 bg-white shadow-sm transition-colors peer-hover:border-slate-400 peer-focus-visible:ring-2 peer-focus-visible:ring-violet-200 peer-checked:border-violet-600 peer-checked:bg-violet-600" />
                            <svg
                              viewBox="0 0 16 16"
                              className="pointer-events-none absolute h-2.5 w-2.5 text-white opacity-0 transition-opacity peer-checked:opacity-100"
                              aria-hidden="true"
                            >
                              <path
                                d="M3.5 8.5L6.3 11.3L12.5 5.1"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </label>
                          <span
                            className="min-w-0 max-w-full truncate text-xs font-medium leading-snug text-slate-800"
                            title={account.account_name}
                          >
                            🏢 {"  "}
                            <span className="ml-1">{account.account_name}</span>
                          </span>
                        </div>

                        {account.hasData ? (
                          <>
                            {/* Score (overall) — first data column */}
                            <div
                              className={cn(
                                "sticky z-[54] flex min-w-0 flex-row items-center justify-center gap-1 border-b border-l border-r border-slate-200/90 px-0.5 py-1.5",
                                "bg-white",
                              )}
                              style={{ position: "sticky", left: accountStickyLeft }}
                            >
                              <BubbleCell
                                score={account.overallScore}
                                isOverall={true}
                                size={isMobile ? 28 : 36}
                                showTooltip={true}
                                tooltipTitle="Score"
                                hasDigest={!!account.hasDigest}
                                isClickable={
                                  isDigitalocean ||
                                  (!!account.hasDigest && !!account.hasData)
                                }
                                onClick={
                                  isDigitalocean
                                    ? () =>
                                        setNativeDetail({
                                          accountId: account.account_id,
                                          categoryId: "overall",
                                        })
                                    : undefined
                                }
                                categories={account.categoryScores.map(
                                  (cs) => ({
                                    name: getCategoryLabel(cs.category),
                                    score: cs.score,
                                    weight:
                                      configuration.categoryWeights[
                                        cs.category
                                      ] || 0,
                                  }),
                                )}
                              />
                            </div>

                            {/* Metric Bubbles */}
                            {metricColumns.map((col) => {
                              const score = getMetricValueForAccount(
                                account,
                                col.metric_id,
                              );
                              const rawMetric = account.rawMetrics.find(
                                (m) => m.metric_id === col.metric_id,
                              );
                              const isCategoryBoundary =
                                categoryBoundaryMetricIds.has(col.metric_id);
                              const isCategoryEnd = categoryLastMetricIds.has(
                                col.metric_id,
                              );
                              return (
                                <div
                                  key={col.metric_id}
                                  className={cn(
                                    "flex min-w-0 min-h-[2.75rem] justify-center items-center border-b bg-white px-0.5 py-1.5 overflow-hidden group-hover:bg-slate-50/50",
                                    !isCategoryEnd && "border-r border-slate-200/70",
                                    isCategoryBoundary && CATEGORY_BORDER_CLASS,
                                  )}
                                >
                                  <BubbleCell
                                    score={score}
                                    isOverall={false}
                                    size={isMobile ? 24 : 30}
                                    showTooltip={true}
                                    tooltipTitle={col.metric_name}
                                    isMetricView={true}
                                    metrics={[
                                      {
                                        name: col.metric_name,
                                        value: score,
                                        weight:
                                          configuration.metricWeights[
                                            col.category_id
                                          ]?.[col.metric_id] || 0,
                                        actualValue:
                                          rawMetric?.value ?? undefined,
                                        uom:
                                          rawMetric?.unit_of_measure ??
                                          undefined,
                                      },
                                    ]}
                                  />
                                </div>
                              );
                            })}
                          </>
                        ) : (
                          <>
                            {/* No data — gray “–” score + empty metric dots */}
                            <div
                              className={cn(
                                "sticky z-[54] flex min-w-0 flex-row items-center justify-center gap-1 border-b border-l border-r border-slate-200/90 px-0.5 py-1.5",
                                "bg-white",
                              )}
                              style={{
                                position: "sticky",
                                left: accountStickyLeft,
                              }}
                            >
                              <BubbleCell
                                score={null}
                                isOverall={true}
                                size={isMobile ? 28 : 36}
                                showTooltip={true}
                                tooltipTitle="Score"
                              />
                            </div>
                            {metricColumns.map(col => {
                              const isCategoryBoundary =
                                categoryBoundaryMetricIds.has(col.metric_id);
                              const isCategoryEnd = categoryLastMetricIds.has(
                                col.metric_id,
                              );
                              return (
                                <div
                                  key={col.metric_id}
                                  className={cn(
                                    "flex min-w-0 min-h-[2.75rem] justify-center items-center border-b bg-white px-0.5 py-1.5 overflow-hidden group-hover:bg-slate-50/50",
                                    !isCategoryEnd &&
                                      "border-r border-slate-200/70",
                                    isCategoryBoundary && CATEGORY_BORDER_CLASS,
                                  )}
                                >
                                  <BubbleCell
                                    score={null}
                                    isOverall={false}
                                    size={isMobile ? 24 : 30}
                                    showTooltip={true}
                                    tooltipTitle={col.metric_name}
                                    isMetricView={true}
                                  />
                                </div>
                              );
                            })}
                          </>
                        )}
                      </div>

                      {/* Synopsis Row - Expanded Assessment */}
                      {isExpanded && account.hasData && (
                        <div
                          className="relative z-10 grid w-full animate-fade-in justify-items-stretch bg-sky-50/40"
                          style={{ gridTemplateColumns }}
                        >
                          <div
                            className={cn(
                              "sticky left-0 z-[55] min-h-[1.25rem] border-r border-slate-200 bg-white px-1 py-1",
                              accountColumnMinWidth,
                            )}
                            style={{ position: "sticky", left: 0 }}
                          />
                          <div
                            className="sticky z-[55] min-h-[1.25rem] border-r border-l border-slate-200 bg-white px-1 py-1"
                            style={{ position: "sticky", left: accountStickyLeft }}
                          />
                          <div
                            className="px-3 py-2 min-w-0"
                            style={{ gridColumn: "3 / -1" }}
                          >
                            {hasAssessments ? (
                              <p className="text-xs font-medium text-violet-700 leading-snug line-clamp-2">
                                {account.aiCategoryAssessments?.["overall"] ||
                                  Object.values(
                                    account.aiCategoryAssessments || {},
                                  )
                                    .filter(Boolean)
                                    .join(" | ") ||
                                  "No assessment available"}
                              </p>
                            ) : (
                              <p className="text-xs font-medium text-muted-foreground italic leading-snug">
                                Please generate an AI-powered Intelligence
                                Briefing to get an Executive Summary for this
                                account.
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </div>

        </CardContent>
      </Card>

      {selectedCompareAccountIds.length > 0 && !isCompareModalOpen
        ? (() => {
            const strip = (
              <KpiCompareSelectionStrip
                placement={selectionStripHost ? "footer" : "overlay"}
                panelRef={selectionStripHost ? undefined : selectionPanelRef}
                selectedAccounts={selectedCompareAccounts}
                minCompareAccounts={MIN_COMPARE_ACCOUNTS}
                canCompare={
                  selectedCompareAccountIds.length >= MIN_COMPARE_ACCOUNTS
                }
                showSpiderViewHint={false}
                onCompare={handleCompareClick}
                onRemoveAccount={toggleCompareSelection}
                onClear={() => {
                  setSelectedCompareAccountIds([]);
                  onHighlightedAccountIdsChange?.([]);
                }}
                onSaveAsWatchlist={
                  onSaveAsWatchlist
                    ? () => onSaveAsWatchlist(selectedCompareAccountIds)
                    : undefined
                }
              />
            );
            return selectionStripHost
              ? createPortal(strip, selectionStripHost)
              : strip;
          })()
        : null}
      </div>

      <Dialog open={isCompareModalOpen} onOpenChange={setIsCompareModalOpen}>
        <DialogContent
          hideClose
          className="flex max-h-[92vh] w-[95vw] max-w-[980px] flex-col overflow-hidden rounded-[14px] border border-slate-200 bg-white p-0 shadow-2xl"
        >
          <SpiderCompareModalContent
            allAccounts={sortedData}
            selectedAccountIds={selectedCompareAccountIds}
            categoryOrder={compareCategories}
            compareColors={COMPARE_COLORS}
            onToggleAccount={toggleCompareSelection}
            onClose={() => setIsCompareModalOpen(false)}
            validationMessage={compareSelectionMessage}
          />
        </DialogContent>
      </Dialog>

      <KpiCompareMissingDataModal
        isOpen={isMissingKpiDataModalOpen}
        onClose={() => setIsMissingKpiDataModalOpen(false)}
        onRemoveAndCompare={handleRemoveAccountsWithoutDataAndCompare}
        accountNames={selectedAccountsWithoutKpiData.map(
          account => account.account_name
        )}
        allSelectedLackData={
          selectedCompareAccounts.length > 0 &&
          selectedAccountsWithoutKpiData.length ===
            selectedCompareAccounts.length
        }
      />

      {isDigitalocean ? (
        <KpiCategoryDetailModal
          open={Boolean(nativeDetail)}
          onClose={() => setNativeDetail(null)}
          accountName={nativeAccountName}
          accountId={nativeDetail?.accountId}
          briefing={nativeBriefing}
          loading={nativeBriefingLoading}
          error={nativeBriefingError}
          categoryId={nativeDetail?.categoryId ?? "overall"}
          onCategoryIdChange={categoryId =>
            setNativeDetail(s => (s ? { ...s, categoryId } : s))
          }
          onPrevAccount={
            nativeAccountIndex > 0
              ? () =>
                  setNativeDetail(s =>
                    s
                      ? {
                          ...s,
                          accountId: sortedData[nativeAccountIndex - 1]
                            .account_id,
                        }
                      : s
                  )
              : undefined
          }
          onNextAccount={
            nativeAccountIndex >= 0 &&
            nativeAccountIndex < sortedData.length - 1
              ? () =>
                  setNativeDetail(s =>
                    s
                      ? {
                          ...s,
                          accountId: sortedData[nativeAccountIndex + 1]
                            .account_id,
                        }
                      : s
                  )
              : undefined
          }
          canPrevAccount={nativeAccountIndex > 0}
          canNextAccount={
            nativeAccountIndex >= 0 &&
            nativeAccountIndex < sortedData.length - 1
          }
        />
      ) : null}
    </>
  );
};
